import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { ANNUAL } from 'pricing-pages-ui-components/constants/Term';
import Raven from 'raven-js';
import { FetchPurchaseMotionRequestTypes } from '../actions/PurchaseMotionActions';
import { hasPurchaseMotionsSucceededSelector } from './LoadingStateSelectors';
import { isMarketingContactsQuantityPackSelector, isQuantityUpgradeSelector, productForSkuIdSelector } from './ProductsSelectors';
import { makeRequestStatusSelector } from './RequestStatusSelectors';
export const fetchPurchaseMotionStatus = makeRequestStatusSelector(FetchPurchaseMotionRequestTypes.REQUEST);
export const allPurchaseMotionsSelector = state => {
  return state.purchaseMotionsByLocation;
};
export const talkToSalesTypeSelector = state => {
  try {
    const allPurchaseMotions = allPurchaseMotionsSelector(state);
    if (allPurchaseMotions) {
      if (Object.keys(allPurchaseMotions).length === 0) {
        return 'TALK_TO_SALES_FALLBACK';
      }
      for (const location of Object.keys(allPurchaseMotions)) {
        var _allPurchaseMotions$l;
        const purchaseMotionsForLocation = (_allPurchaseMotions$l = allPurchaseMotions[location]) === null || _allPurchaseMotions$l === void 0 ? void 0 : _allPurchaseMotions$l.purchaseMotions;
        if (!purchaseMotionsForLocation) continue;
        for (const apiName of Object.keys(purchaseMotionsForLocation)) {
          const purchaseMotionsObjectArray = purchaseMotionsForLocation[apiName] || [];
          const purchaseMotions = Array.isArray(purchaseMotionsObjectArray) ? purchaseMotionsObjectArray.map(({
            key
          }) => key) : [];
          if (purchaseMotions.includes('TALK_TO_SALES')) {
            return 'TALK_TO_SALES';
          }
          if (purchaseMotions.includes('TALK_TO_SALES_FALLBACK')) {
            return 'TALK_TO_SALES_FALLBACK';
          }
        }
      }
    }
  } catch (error) {
    Raven.captureException(new Error('Error accessing purchase motions', {
      cause: error
    }));
  }
  return 'TALK_TO_SALES_FALLBACK';
};

// For Seats Marketing Hub, it's possible to have 2 touchless purchase motions -
// Buy Additional Contacts + Buy Additional Seats. For enterprise, contacts are
// not available for touchless purchase. However, it's possible to still add
// contacts via the calculator. The Calculator then sees Buy Additional Seats
// as a touchless purchase motion, so the customer can attempt to go to the
// interstitial for touchless checkout.
export const canExplicitlyBuyMarketingContacts = (state, {
  skuId,
  location,
  purchaseMotions
}) => {
  const isMarketingContactsQuantityPack = isMarketingContactsQuantityPackSelector(state, {
    skuId
  });
  // Still want to display Buy Additional Seats on the merch card, not TTS
  if (!isMarketingContactsQuantityPack || location === 'MERCH_CARD') return true;
  return Boolean(purchaseMotions.find(purchaseMotion => ['BUY_TOUCHLESS', 'BUY_ADDITIONAL_CONTACTS'].includes(String(purchaseMotion))));
};
const getPurchaseMotionsSafely = (allPurchaseMotions, location, apiName) => {
  try {
    var _allPurchaseMotions$l2;
    if (apiName && allPurchaseMotions !== null && allPurchaseMotions !== void 0 && (_allPurchaseMotions$l2 = allPurchaseMotions[location]) !== null && _allPurchaseMotions$l2 !== void 0 && (_allPurchaseMotions$l2 = _allPurchaseMotions$l2.purchaseMotions) !== null && _allPurchaseMotions$l2 !== void 0 && _allPurchaseMotions$l2[apiName]) {
      return allPurchaseMotions[location].purchaseMotions[apiName] || [];
    }
    return [];
  } catch (error) {
    Raven.captureException(new Error('Error accessing purchase motions', {
      cause: error
    }));
    return [];
  }
};
export const purchaseMotionsSelector = (state, {
  location = 'MERCH_CARD',
  skuId
}) => {
  const {
    apiName
  } = productForSkuIdSelector(state, {
    skuId
  });
  const isMarketingContactsQuantityPack = isMarketingContactsQuantityPackSelector(state, {
    skuId
  });
  const allPurchaseMotions = allPurchaseMotionsSelector(state);
  const purchaseMotions = getPurchaseMotionsSafely(allPurchaseMotions, location, apiName);
  const originalPurchaseMotions = purchaseMotions.map(({
    key
  }) => key);
  if (isMarketingContactsQuantityPack) {
    const canBuyContactsExplicitly = canExplicitlyBuyMarketingContacts(state, {
      skuId,
      location,
      purchaseMotions: originalPurchaseMotions
    });
    if (canBuyContactsExplicitly) {
      return originalPurchaseMotions;
    } else {
      return [talkToSalesTypeSelector(state)];
    }
  }
  if (!originalPurchaseMotions.length) {
    return [talkToSalesTypeSelector(state)];
  }
  return originalPurchaseMotions;
};
const hasNoPurchaseMotionsSelector = state => {
  const purchaseMotions = allPurchaseMotionsSelector(state);
  const hasPurchaseMotionsSucceeded = hasPurchaseMotionsSucceededSelector(state);
  return hasPurchaseMotionsSucceeded && Object.keys(purchaseMotions).length === 0;
};
const TOUCHLESS_PURCHASE_MOTION_VARIANTS = ['BUY_TOUCHLESS', 'BUY_ADDITIONAL_SEATS', 'BUY_ADDITIONAL_CONTACTS'];
export const purchaseMotionsContainTouchlessVariant = purchaseMotions => {
  return purchaseMotions.some(purchaseMotion => TOUCHLESS_PURCHASE_MOTION_VARIANTS.includes(String(purchaseMotion)));
};
export const skuIdsContainTouchlessPurchaseMotionVariants = (state, {
  skuIds,
  location
}) => {
  return skuIds.every(skuId => {
    const purchaseMotions = purchaseMotionsSelector(state, {
      skuId,
      location
    });
    return purchaseMotionsContainTouchlessVariant(purchaseMotions);
  });
};

// We can send quantity upgrades that don't have matching purchase motions
// (buyAdditionalSeats and buyAdditionalContacts) together to Shopping
export const skuIdsAreQuantityUpgradeMixedTouchlessPurchase = (state, {
  skuIds,
  location = 'CALCULATOR'
}) => {
  const allTouchlessVariants = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  if (!allTouchlessVariants) return false;
  const productConfigurations = skuIds.map(skuId => createProductConfig({
    skuId
  }));
  return isQuantityUpgradeSelector(state, {
    productConfigurations
  });
};
export const canSkuIdsBePurchasedTouchlesslyTogether = (state, {
  skuIds,
  location = 'MERCH_CARD'
}) => {
  const isUpgradeable = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  const isQuantityUpgrade = skuIdsAreQuantityUpgradeMixedTouchlessPurchase(state, {
    skuIds,
    location
  });
  return isUpgradeable || isQuantityUpgrade;
};

/**
 * @deprecated - this selector should only be used in plan builder
 */
export const commonPurchaseMotionsSelector = (state, {
  skuIds,
  location,
  queryParamTerm = ANNUAL
}) => {
  const foundPurchaseMotions = {};
  skuIds.forEach(skuId => purchaseMotionsSelector(state, {
    location,
    skuId
  }).forEach(purchaseMotion => {
    if (purchaseMotion in foundPurchaseMotions) {
      foundPurchaseMotions[String(purchaseMotion)]++;
    } else {
      foundPurchaseMotions[String(purchaseMotion)] = 1;
    }
  }));
  let commonPurchaseMotions = Object.keys(foundPurchaseMotions).filter(purchaseMotion => foundPurchaseMotions[purchaseMotion] === skuIds.length);
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  const isMixedTouchlessPurchase = canSkuIdsBePurchasedTouchlesslyTogether(state, {
    skuIds,
    location,
    queryParamTerm
  });
  if (isMixedTouchlessPurchase && !purchaseMotionsContainTouchlessVariant(commonPurchaseMotions)) {
    commonPurchaseMotions = ['BUY_TOUCHLESS', ...commonPurchaseMotions];
  }
  if (!commonPurchaseMotions.length && !hasNoPurchaseMotions) {
    return [talkToSalesTypeSelector(state)];
  }
  return commonPurchaseMotions;
};